/**
 * Toast (RACCPE-20, shared deals primitives)
 *
 * A minimal, accessible toast system for transient success/failure feedback —
 * built for the deal review/extension/expiry flows (CHP-223/233/225) that
 * replace the old `window.confirm()` round-trips.
 *
 * Positioning (CHP-326): the viewport is pinned TOP-RIGHT with a safe inset so
 * toasts never overlap the page's primary content/actions, which were
 * previously obscured by the centred viewport. Multiple toasts stack
 * vertically (newest at the top of the list, i.e. nearest the corner) with a
 * consistent gap. A high z-index keeps the stack above drawers/dialogs, and
 * `pointer-events-none` on the viewport (with `pointer-events-auto` per toast)
 * means the empty area around the toasts never blocks clicks on the page.
 *
 * Accessibility: the viewport is rendered at all times as a single polite live
 * region (`role="status"` implies `aria-live="polite"`). Screen readers
 * announce toasts as they are appended to the existing region rather than
 * needing the region to mount on demand. `aria-atomic="false"` so only the
 * newly added toast is read, not the whole stack.
 */

import { X } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type ToastVariant = "default" | "success" | "destructive";

export interface ToastOptions {
  /** Optional bold lead line above the description. */
  title?: string;
  description: string;
  variant?: ToastVariant;
  /** ms before auto-dismiss; 0 keeps it until dismissed. Default 5000. */
  duration?: number;
}

interface ToastRecord extends ToastOptions {
  id: number;
}

interface ToastContextValue {
  toast: (opts: ToastOptions) => number;
  dismiss: (id: number) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within a <ToastProvider>");
  }
  return ctx;
}

const DEFAULT_DURATION = 5000;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<ToastRecord[]>([]);
  const idRef = React.useRef(0);
  const timers = React.useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = React.useCallback((id: number) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const toast = React.useCallback(
    (opts: ToastOptions) => {
      const id = (idRef.current += 1);
      setToasts((cur) => [...cur, { ...opts, id }]);
      const duration = opts.duration ?? DEFAULT_DURATION;
      if (duration > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), duration),
        );
      }
      return id;
    },
    [dismiss],
  );

  // Clear any outstanding auto-dismiss timers on unmount.
  React.useEffect(() => {
    const timers_ = timers.current;
    return () => {
      timers_.forEach((t) => clearTimeout(t));
      timers_.clear();
    };
  }, []);

  const value = React.useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

const VARIANT_STYLES: Record<ToastVariant, string> = {
  default: "border-border bg-background text-foreground",
  // Semantic tokens (dark-mode safe) — matches the badge `success` variant.
  success: "border-success-border bg-success-muted text-success-muted-foreground",
  destructive: "border-destructive/30 bg-destructive/10 text-destructive",
};

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: ToastRecord[];
  onDismiss: (id: number) => void;
}) {
  return (
    <ol
      role="status"
      aria-live="polite"
      aria-atomic="false"
      aria-label="Notifications"
      className="pointer-events-none fixed right-4 top-4 z-[100] flex max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-sm flex-col items-stretch gap-2 overflow-hidden"
    >
      {toasts.map((t) => (
        <li
          key={t.id}
          className={cn(
            "pointer-events-auto flex w-full items-start gap-3 rounded-lg border p-4 shadow-overlay",
            VARIANT_STYLES[t.variant ?? "default"],
          )}
        >
          <div className="min-w-0 flex-1 text-sm">
            {t.title && <p className="font-semibold">{t.title}</p>}
            <p className={cn(t.title && "text-muted-foreground")}>{t.description}</p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="-mr-1 -mt-1 shrink-0"
            aria-label="Dismiss notification"
            onClick={() => onDismiss(t.id)}
          >
            <X />
          </Button>
        </li>
      ))}
    </ol>
  );
}

/**
 * Shared deal-action toast copy (CHP-326).
 *
 * Centralises the success/failure messages for the deal lifecycle so every
 * call-site renders the same wording and `variant`. These are the messages the
 * consolidation sweep should adopt across the wizard / action forms / feed so
 * that, e.g., "marked Closed-Lost" reads identically whether a manager or a
 * partner triggers it. Each helper returns a ready-to-spread {@link ToastOptions}:
 *
 *   const { toast } = useToast();
 *   toast(dealToasts.registered(deal.client_name));
 *
 * Pass the client/partner name the surface already has in hand. Helpers that
 * take a second name accept the partner/reseller label.
 */
export const dealToasts = {
  // CHP-361: terse BDM/PO submission confirmation (drops client-name interpolation).
  registered: (): ToastOptions => ({
    variant: "success",
    description: "Registration submitted",
  }),
  // CHP-974: BDM/PO leaves the wizard with an unsubmitted draft (the draft
  // auto-saves per completed step, so exiting loses nothing).
  draftSaved: (): ToastOptions => ({
    variant: "success",
    description: "Progress saved as draft",
  }),
  // CHP-360: terse CM/RA/RM registration-review outcomes (drop client-name
  // interpolation). CHP-641: verb normalised to Deny per Flow B.
  registrationApproved: (): ToastOptions => ({
    variant: "success",
    description: "Registration approved",
  }),
  registrationDenied: (): ToastOptions => ({
    variant: "destructive",
    description: "Registration denied",
  }),
  // CHP-809: RM-only systemic force-bypass override (Flow B "Manual
  // Adjudication" → PRIVILEGED OVERRIDES). A force-bypass IS an approval, so
  // the success copy reads as an approval outcome.
  forceBypassApplied: (): ToastOptions => ({
    variant: "success",
    description: "Force bypass applied — registration approved",
  }),
  // CHP-361: BDM/PO extension request (new target date).
  extensionRequested: (): ToastOptions => ({
    variant: "success",
    description: "Extension requested",
  }),
  // CHP-1179: BDM/PO withdraws their own pending extension request.
  extensionWithdrawn: (): ToastOptions => ({
    variant: "success",
    description: "Extension request withdrawn",
  }),
  // CHP-357: the CM/RA/RM extension-review outcome toasts.
  extensionApproved: (): ToastOptions => ({
    variant: "success",
    description: "Extension approved",
  }),
  // Edit & Approve path — a distinct toast from the plain approve (CHP-357).
  extensionExpiryUpdated: (): ToastOptions => ({
    variant: "success",
    description: "Expiry date updated",
  }),
  extensionDenied: (): ToastOptions => ({
    variant: "destructive",
    description: "Request denied",
  }),
  // CHP-405: manager-direct close-won routes through win-approve (LIVE →
  // win submission for approval), so the copy reads "submitted for approval"
  // rather than "confirmed".
  closedWon: (): ToastOptions => ({
    variant: "success",
    description: "Submitted as closed-won for approval.",
  }),
  // CHP-361: BDM/PO "put deal as closed-lost" outcome (DealWinActions). Distinct
  // helper from the manager-direct `closedLost` so the manager close drawer
  // keeps its own copy.
  statusClosedLost: (): ToastOptions => ({
    variant: "success",
    description: "Status updated to Closed-Lost",
  }),
  // CHP-358: the CM/RA/RM closed-won REVIEW outcome toasts. Distinct from the
  // manager-direct close (closedWon) — this is the reviewer approving/rejecting
  // a partner's closed-won request.
  winReviewApproved: (): ToastOptions => ({
    variant: "success",
    description: "Request approved",
  }),
  winReviewDenied: (): ToastOptions => ({
    variant: "destructive",
    description: "Request denied",
  }),
  // CHP-405: manager-direct close-lost terse confirmation.
  closedLost: (): ToastOptions => ({
    variant: "success",
    description: "Marked as closed-lost.",
  }),
  // CHP-361: BDM/PO "apply for closed-won status" outcome.
  winRequested: (): ToastOptions => ({
    variant: "success",
    description: "Closed-Won request submitted",
  }),
  // CHP-429: BDM/PO "Log deal progress" entry recorded.
  progressLogged: (): ToastOptions => ({
    variant: "success",
    description: "Deal progress logged",
  }),
  // CHP-464 (finishing CHP-387): direct-payout BDM submits the first-meeting
  // reward claim from the midpoint card.
  meetingRewardClaimed: (): ToastOptions => ({
    variant: "success",
    description: "Meeting reward claim submitted for review",
  }),
  // CHP-361: BDM/PO expiry "leave expiry as is" outcome.
  letExpire: (): ToastOptions => ({
    variant: "success",
    description: "Request processed",
  }),
  // CHP-361: BDM/PO expiry "set to closed-lost" outcome (the "close deal now"
  // path on the expiry decision overlay).
  dealClosed: (): ToastOptions => ({
    variant: "success",
    description: "Status updated to Closed-Lost",
  }),
  failed: (action: string): ToastOptions => ({
    variant: "destructive",
    description: `Couldn't ${action}. Refresh and try again.`,
  }),
} as const;
